import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowRight, CheckCircle, Rocket, Sparkles, Gift, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { trackClick } from '../services/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const WaitlistSection = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interestType, setInterestType] = useState('user');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { t, isRTL } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !name) return;
    
    // Track click
    trackClick('waitlist-section-submit', 'Join Waitlist', 'Landing Page');
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/waitlist`, { 
        name, 
        email, 
        interest_type: interestType, 
        utm_source: new URLSearchParams(window.location.search).get('utm_source') || 'direct' 
      });
      setSubmitted(true);
      toast.success('Welcome to Qykfin!');
    } catch (error) {
      if (error.response?.status === 400) { 
        toast.info('Already on waitlist!'); 
        setSubmitted(true); 
      } else { 
        toast.error('Something went wrong.'); 
      }
    }
    setLoading(false);
  };

  const benefits = [
    { icon: Gift, text: t.waitlist.benefits[0] },
    { icon: Sparkles, text: t.waitlist.benefits[1] },
    { icon: Shield, text: t.waitlist.benefits[2] }
  ];

  return (
    <section id="waitlist" className="py-20 md:py-24 bg-[#1D1D1F] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#0071E3]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#5856D6]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 relative z-10">
        <div className="max-w-xl mx-auto text-center">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Rocket className="w-3.5 h-3.5 text-[#0071E3]" />
            <span className="text-[11px] font-semibold text-white/80 uppercase tracking-wide font-inter">
              {t.waitlist.badge}
            </span>
          </div>

          {/* Headline */}
          <h2 className="font-dm text-3xl md:text-4xl lg:text-[42px] font-semibold tracking-tight text-white mb-4 leading-tight">
            {t.waitlist.title}
          </h2>
          <p className="text-lg text-white/60 font-inter mb-8">
            {t.waitlist.subtitle}
          </p>

          {/* Benefits */}
          <div className={`flex flex-wrap justify-center gap-4 mb-10 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className={`flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <benefit.icon className="w-4 h-4 text-[#34C759]" />
                <span className="text-white/80 text-[13px] font-inter">{benefit.text}</span>
              </div>
            ))}
          </div>

          {/* Form */}
          {!submitted ? (
            <div className={`bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10 ${isRTL ? 'text-right' : ''}`}>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  placeholder={t.waitlist.namePlaceholder}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className={`h-12 px-5 rounded-xl border-white/20 bg-white/5 text-white placeholder:text-white/40 text-[15px] focus:border-white/40 focus:ring-0 ${isRTL ? 'text-right' : ''}`}
                  data-testid="waitlist-name-input"
                />
                <Input
                  type="email"
                  placeholder={t.waitlist.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`h-12 px-5 rounded-xl border-white/20 bg-white/5 text-white placeholder:text-white/40 text-[15px] focus:border-white/40 focus:ring-0 ${isRTL ? 'text-right' : ''}`}
                  data-testid="waitlist-email-input"
                />
                <Select value={interestType} onValueChange={setInterestType}>
                  <SelectTrigger className={`h-12 px-5 rounded-xl border-white/20 bg-white/5 text-white text-[15px] focus:border-white/40 focus:ring-0 ${isRTL ? 'text-right flex-row-reverse' : ''}`}>
                    <SelectValue placeholder="I am a..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2D2D2F] border-white/20">
                    <SelectItem value="user" className="text-white hover:bg-white/10">{t.waitlist.userTypes.user}</SelectItem>
                    <SelectItem value="investor" className="text-white hover:bg-white/10">{t.waitlist.userTypes.investor}</SelectItem>
                    <SelectItem value="partner" className="text-white hover:bg-white/10">{t.waitlist.userTypes.partner}</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  type="submit"
                  disabled={loading}
                  className={`w-full h-12 bg-gradient-to-r from-[#0071E3] to-[#5856D6] text-white hover:opacity-90 rounded-xl font-medium text-[15px] flex items-center justify-center gap-2 transition-all hover:scale-[1.01] ${isRTL ? 'flex-row-reverse' : ''}`}
                  data-testid="waitlist-submit-btn"
                >
                  {loading ? 'Joining...' : t.waitlist.cta}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </Button>
              </form>

              <p className="text-center text-white/30 text-[12px] font-inter mt-5">
                {t.waitlist.privacy}
              </p>
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
              <div className="w-16 h-16 bg-[#34C759]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#34C759]" />
              </div>
              <h3 className="text-2xl font-dm font-semibold text-white mb-2">{t.waitlist.successTitle}</h3>
              <p className="text-white/60 font-inter">{t.waitlist.successSubtitle}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WaitlistSection;
